// src/CameraManager.jsx

import { useEffect, useRef, useContext } from "react";
import { useThree } from "@react-three/fiber";
import { CameraControls } from "@react-three/drei";
import { ControlContext } from "./ControlContext";
import * as THREE from "three";

// Room bounding mesh dimensions (static)
const ROOM_BOUNDING_SIZE = {
  width: 310,   // Adjust to frame your room horizontally
  height: 90,   // Adjust to frame your room vertically
  depth: 100,   // Depth doesn't matter much for front view
};

const CAMERA_PRESETS = {
  Detail: {
    // position: [30, 20, 20],
    limits: {
      minPolarAngle: Math.PI / 3,
      maxPolarAngle: (3 * Math.PI) / 4,
      minAzimuthAngle: -Math.PI / 6,
      maxAzimuthAngle: Math.PI / 6,
    },
    dollySpeed: 0,
  },
  Room: {
    position: [0, 0, 900],
    target: [0, 0, 0],
    limits: {
      minDistance: 430,
      maxDistance: 774,
      minPolarAngle: Math.PI / 2,
      maxPolarAngle: Math.PI / 2,
      minAzimuthAngle: -Math.PI / 12,
      maxAzimuthAngle: Math.PI / 12,
    },
    dollySpeed: 0,
  },
  Debug: {
    position: [0, 0, 200],
    target: [0, 0, 0],
    limits: {
      minDistance: 0,
      maxDistance: 5000,
      minPolarAngle: 0,
      maxPolarAngle: Math.PI,
      minAzimuthAngle: -Infinity,
      maxAzimuthAngle: Infinity,
    },
    dollySpeed: 1,
  },
};

function SceneCamera({ cameraType = "Frame" }) {
  const controlsRef = useRef();
  const { gl, camera, scene, size } = useThree();
  const {
    cameraDetailW,
    cameraDetailH,
    cameraDetailX,
    cameraDetailY,
    printWidth,
    printHeight,
    borderWidth,
    borderHeight,
    frameViewPadding,
    maxFrameSize,
    boundingMesh,
  } = useContext(ControlContext);

  const DEFAULT_POSITION = [0, 0, 200];
  const DEFAULT_TARGET = [0, 0, 0];
  const FRAME_VIEW_PADDING = frameViewPadding;

  const setFrontView = () => {
    if (controlsRef.current) {
      controlsRef.current.setLookAt(
        ...DEFAULT_POSITION,
        ...DEFAULT_TARGET,
        true
      );
      if (boundingMesh) {
        const box = new THREE.Box3().setFromObject(boundingMesh);
        const size = new THREE.Vector3();
        box.getSize(size);
        const maxDimension = Math.max(size.x, size.y);
        // const padding = maxDimension * FRAME_VIEW_PADDING;
        const padding = 0;
        controlsRef.current.fitToBox(boundingMesh, true, {
          paddingLeft: padding,
          paddingRight: padding,
          paddingBottom: padding,
          paddingTop: padding,
        });
      }
    }
  };

  useEffect(() => {
    window.setFrameView = () => {
      if (controlsRef.current) {
        controlsRef.current.smoothTime = 0;
        setFrontView();
        setTimeout(() => {
          controlsRef.current.smoothTime = 0.3;
        }, 500);
      }
    };
  }, [printWidth, printHeight, borderWidth, borderHeight, scene]);

  useEffect(() => {
    window.takeScreenshot = (maxSize = 512) => {
      // Hide environment objects
      const hiddenObjects = [];
      scene.traverse((child) => {
        if (child.userData.isEnvironment) {
          hiddenObjects.push(child);
          child.visible = false;
        }
      });

      // Store environment settings
      const oldEnv = scene.environment;
      const oldBg = scene.background;
      scene.environment = null;
      scene.background = null;
      const oldClearColor = gl.getClearColor(new THREE.Color());
      const oldAlpha = gl.getClearAlpha();
      gl.setClearColor(0x000000, 0);

      // Store original canvas state
      const { width: oldWidth, height: oldHeight } = gl.getSize(
        new THREE.Vector2()
      );
      const oldPixelRatio = gl.getPixelRatio();

      // IMPORTANT: Store original camera state and controls state
      const oldCameraPosition = camera.position.clone();
      const oldCameraQuaternion = camera.quaternion.clone();
      const oldCameraAspect = camera.aspect;

      // Get bounding mesh dimensions
      const box = new THREE.Box3().setFromObject(boundingMesh);
      const size = new THREE.Vector3();
      box.getSize(size);
      const center = new THREE.Vector3();
      box.getCenter(center);
      const meshAspect = size.x / size.y;

      // Set dimensions based on mesh aspect ratio
      let width, height;
      if (meshAspect >= 1) {
        width = maxSize;
        height = maxSize / meshAspect;
      } else {
        height = maxSize;
        width = maxSize * meshAspect;
      }

      // Create a temporary camera for the screenshot
      const screenshotCamera = camera.clone();
      screenshotCamera.aspect = width / height;
      screenshotCamera.updateProjectionMatrix();

      // Calculate optimal camera position
      const fov = screenshotCamera.fov * (Math.PI / 180);
      const maxDim = Math.max(size.x / screenshotCamera.aspect, size.y);
      const distance = (maxDim / 2 / Math.tan(fov / 2)) * 1.05; // 5% margin

      // Position camera directly in front of object
      screenshotCamera.position.set(center.x, center.y, center.z + distance);
      screenshotCamera.lookAt(center);

      // Set canvas size for screenshot
      gl.setPixelRatio(2);
      gl.setSize(width, height, false);

      // Render with screenshot camera
      gl.render(scene, screenshotCamera);

      // Get image data
      const imageDataUrl = gl.domElement.toDataURL("image/png", 1.0);

      // Restore everything
      hiddenObjects.forEach((obj) => (obj.visible = true));
      scene.environment = oldEnv;
      scene.background = oldBg;
      gl.setClearColor(oldClearColor, oldAlpha);

      // Restore canvas size and pixel ratio
      gl.setSize(oldWidth, oldHeight, false);
      gl.setPixelRatio(oldPixelRatio);

      // Restore camera properties
      camera.position.copy(oldCameraPosition);
      camera.quaternion.copy(oldCameraQuaternion);
      camera.aspect = oldCameraAspect;
      camera.updateProjectionMatrix();

      // Restore controls to proper framing with padding
      if (controlsRef.current && boundingMesh) {
        // Make sure we're using the original framing approach
        const maxDimension = Math.max(size.x, size.y);
        const padding = maxDimension * FRAME_VIEW_PADDING;

        controlsRef.current.fitToBox(boundingMesh, true, {
          paddingLeft: padding,
          paddingRight: padding,
          paddingBottom: padding,
          paddingTop: padding,
        });
      }

      return imageDataUrl;
    };
  }, [scene, camera, gl, boundingMesh]);

  useEffect(() => {
    // A single function that does everything in one go:
    window.takeSquareScreenshot = (size = 512) => {
      // 1) Hide environment/wall/furniture (tagged as isEnvironment)
      const hiddenObjects = [];
      scene.traverse((child) => {
        if (child.userData.isEnvironment) {
          hiddenObjects.push(child);
          child.visible = false;
        }
      });
      // 2) Temporarily remove the HDRI environment
      const oldEnv = scene.environment;
      const oldBg = scene.background;
      scene.environment = null;
      scene.background = null;

      const oldClearColor = gl.getClearColor(new THREE.Color());
      const oldAlpha = gl.getClearAlpha();
      gl.setClearColor(0x000000, 0);

      // STORE OLD CAMERA & CANVAS STATES
      const oldAspect = camera.aspect;
      const { width: oldWidth, height: oldHeight } = gl.getSize(
        new THREE.Vector2()
      );
      const oldPixelRatio = gl.getPixelRatio();

      // FORCE A 1:1 ASPECT
      camera.aspect = 1;
      camera.updateProjectionMatrix();

      //
      // RESIZE THE CANVAS TO SQUARE
      //
      // Increase pixel ratio for higher-resolution output if desired
      gl.setPixelRatio(4);
      gl.setSize(size, size, false);

      //
      // FIT THE CAMERA TO BOUNDING MESH WITH *REDUCED* PADDING
      //
      if (controlsRef.current && boundingMesh) {
        controlsRef.current.update();
        controlsRef.current.fitToBox(boundingMesh, true, {
          paddingLeft: 0.01 * boundingMesh.scale.x,
          paddingRight: 0.01 * boundingMesh.scale.x,
          paddingTop: 0.01 * boundingMesh.scale.y,
          paddingBottom: 0.01 * boundingMesh.scale.y,
        });
      }

      //
      // RENDER ONCE AND GET THE DATA URL
      //
      gl.render(scene, camera);
      const imageDataUrl = gl.domElement.toDataURL("image/png", 1.0);

      //
      // RESTORE EVERYTHING
      //
      // 1) Environment objects
      hiddenObjects.forEach((obj) => (obj.visible = true));
      scene.environment = oldEnv;
      scene.background = oldBg;
      gl.setClearColor(oldClearColor, oldAlpha);

      // 2) Camera aspect
      camera.aspect = oldAspect;
      camera.updateProjectionMatrix();

      // 3) Canvas size & pixel ratio
      gl.setSize(oldWidth, oldHeight, false);
      gl.setPixelRatio(oldPixelRatio);

      const box = new THREE.Box3().setFromObject(boundingMesh);
      const sizebox = new THREE.Vector3();
      box.getSize(sizebox);
      const maxDimension = Math.max(sizebox.x, sizebox.y);
      const padding = maxDimension * FRAME_VIEW_PADDING;

      if (controlsRef.current && boundingMesh) {
        controlsRef.current.fitToBox(boundingMesh, true, {
          paddingLeft: padding,
          paddingRight: padding,
          paddingBottom: padding,
          paddingTop: padding,
        });
      }

      return imageDataUrl;
    };
  }, [camera, gl, scene, boundingMesh]);

  useEffect(() => {
    if (!controlsRef.current) return;
    const controls = controlsRef.current;

    // console.log("minDistance", controls.minDistance);

    if (cameraType === "Frame") {
      controls.setLookAt(...DEFAULT_POSITION, ...DEFAULT_TARGET, true);

      if (boundingMesh) {
        const box = new THREE.Box3().setFromObject(boundingMesh);
        const size = new THREE.Vector3();
        box.getSize(size);

        const clampedSize = new THREE.Vector3(
          Math.min(size.x, maxFrameSize),
          Math.min(size.y, maxFrameSize),
          Math.min(size.z, maxFrameSize)
        );

        const clampedBoundingMesh = new THREE.Mesh(
          new THREE.BoxGeometry(clampedSize.x, clampedSize.y, clampedSize.z),
          new THREE.MeshBasicMaterial({ visible: false }) // Make it invisible
        );

        const maxDimension = Math.max(clampedSize.x, clampedSize.y);

        controls.minDistance = 0;
        controls.maxDistance = 2000;
        controls.minPolarAngle = Math.PI / 2.2;
        controls.maxPolarAngle = (2 * Math.PI) / 3.5;
        controls.minAzimuthAngle = -Math.PI / 4;
        controls.maxAzimuthAngle = Math.PI / 4;

        // Calculate aspect ratio and apply extra padding for square-ish viewports
        const aspectRatio = camera.aspect;
        const isNearSquare = aspectRatio >= 1 / 1.5 && aspectRatio <= 1.5 / 1;
        const paddingMultiplier = isNearSquare ? 1.5 : 1; // Adjust 1.5 to your preference

        const padding = maxDimension * FRAME_VIEW_PADDING * paddingMultiplier;
        controls.fitToBox(clampedBoundingMesh, true, {
          paddingLeft: padding,
          paddingRight: padding,
          paddingBottom: padding,
          paddingTop: padding,
        });
      }
    } else if (cameraType === "Detail") {
      controls.setLookAt(
        cameraDetailX,
        cameraDetailY,
        100,
        cameraDetailW,
        cameraDetailH,
        0,
        true
      );
      Object.entries(CAMERA_PRESETS.Detail.limits).forEach(([key, value]) => {
        controls[key] = value;
      });

      // Calculate the exact Euclidean distance between camera and target
      const exactDistance = Math.sqrt(
        Math.pow(cameraDetailX - cameraDetailW, 2) +
          Math.pow(cameraDetailY - cameraDetailH, 2) +
          Math.pow(100 - 0, 2)
      );

      // Lock the distance to this exact calculated value
      controls.minDistance = exactDistance;
      controls.maxDistance = exactDistance;

    } else if (cameraType === "Room") {
      // Create a virtual bounding mesh for the room view
      const roomBoundingMesh = new THREE.Mesh(
        new THREE.BoxGeometry(
          ROOM_BOUNDING_SIZE.width,
          ROOM_BOUNDING_SIZE.height,
          ROOM_BOUNDING_SIZE.depth
        ),
        new THREE.MeshBasicMaterial({ visible: false })
      );

      controls.setLookAt(...DEFAULT_POSITION, ...DEFAULT_TARGET, true);

      // Apply Room preset limits FIRST (including distance limits)
      const preset = CAMERA_PRESETS.Room;
      Object.entries(preset.limits).forEach(([key, value]) => {
        controls[key] = value;
      });

      // Set a max distance cap to prevent zooming too far out
      // controls.maxDistance = 900; // Adjust this value to control max zoom out

      // Fit to the room bounding box (will respect maxDistance limit)
      controls.fitToBox(roomBoundingMesh, true, {
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: 0,
        paddingTop: 0,
      });

    } else {
      const preset = CAMERA_PRESETS[cameraType];
      if (!preset) return;

      controls.setLookAt(...preset.position, ...preset.target, true);
      Object.entries(preset.limits).forEach(([key, value]) => {
        controls[key] = value;
      });
    }
  }, [
    cameraType,
    // boundingMesh,
    cameraDetailW,
    cameraDetailH,
    cameraDetailX,
    cameraDetailY,
    printWidth,
    printHeight,
    borderWidth,
    borderHeight,
    size.width,
    size.height,
  ]);

  return (
    <CameraControls
      ref={controlsRef}
      makeDefault
      draggingSmoothTime={0.3}
      verticalDragToForward={false}
      dollyToCursor={cameraType === "Debug"}
      dollySpeed={
        CAMERA_PRESETS[cameraType]?.dollySpeed !== undefined
          ? CAMERA_PRESETS[cameraType].dollySpeed
          : 0 // Default to 0 for safety
      }
      truckSpeed={cameraType === "Debug" ? 1 : 0}
    />
  );
}

export default SceneCamera;
